import { isIterableObject } from '@takeshape/util';
import pick from 'lodash/pick';
import { all, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import type { ImmutableMap } from '../common/utils/immutable-types';
import {
  ADD_ARRAY_ITEM,
  BLUR_FIELD,
  CHANGE_DISCRIMINATOR_FIELD,
  CHANGE_FIELD,
  CLEAR_FORM,
  CUSTOM_VALIDATION,
  REGISTER_FORM,
  REMOVE_ARRAY_ITEM,
  SET_FORM_DATA,
  SKIP_REGISTRATION,
  SUBMIT_FORM,
  SWAP_ARRAY_ITEMS,
  VALIDATE_FORM,
  VALIDATE_ON_BLUR
} from './action-types';
import {
  type AddArrayItemAction,
  type BlurFieldAction,
  type ChangeDiscriminatorFieldAction,
  type ChangeFieldAction,
  type RegisterFormAction,
  type RemoveArrayItemAction,
  type SetFormDataAction,
  type SubmitFormAction,
  type SubmitFormFailureAction,
  type SubmitFormSuccessAction,
  type SwapArrayItemsAction,
  submitForm,
  submitFormSuccess,
  validateForm
} from './actions';
import {
  getAllErrors,
  getAutoSubmit,
  getData,
  getDirtyJS,
  getForm,
  getStructuralChanges,
  getValidateOnBlur,
  getValues
} from './selectors';
import type { Data, ErrorList, FormState, StructuralChange } from './types';
import { takeForm } from './util/take-form';
import { takeFormLatest } from './util/take-form-latest';

export function* handleBlurField(action: BlurFieldAction) {
  const validateOnBlur: boolean = yield select(getValidateOnBlur, {
    formName: action.meta.formName
  });

  if (validateOnBlur) {
    yield put({ type: VALIDATE_ON_BLUR, meta: action.meta });
  }
}

const pauseChanges = new Set([CHANGE_FIELD, SET_FORM_DATA, SWAP_ARRAY_ITEMS]);

export function* validateOnChange(
  action:
    | ChangeFieldAction
    | SetFormDataAction
    | SwapArrayItemsAction
    | SubmitFormAction
    | AddArrayItemAction
    | RemoveArrayItemAction
    | ChangeDiscriminatorFieldAction
) {
  if (action.type === SUBMIT_FORM) {
    return;
  }

  if (pauseChanges.has(action.type)) {
    yield delay(500);
  }

  yield put({ type: VALIDATE_FORM, meta: action.meta });
}

export function* submitFormSaga<T extends Data = Data>(action: SubmitFormAction<T>) {
  const { formName, onlyDirty, withValues, async, invokedDuringAutoSubmit } = action.meta;
  let errors;
  let hasErrors = false;
  if (!invokedDuringAutoSubmit) {
    yield put(validateForm(formName));
    yield takeForm(CUSTOM_VALIDATION, formName);

    const form: ImmutableMap<FormState> = yield select(getForm, { formName });

    errors = form.get('errors');
    hasErrors = Boolean(errors?.size);
    yield put({
      type: `${VALIDATE_FORM}_FULFILLED`,
      meta: { formName },
      payload: errors
    });

    if (action.validateCallback) {
      action.validateCallback(errors?.toJS());
    }
  }

  if (hasErrors) {
    yield put({
      type: `${SUBMIT_FORM}_FAILURE`,
      meta: { formName },
      payload: { errors }
    });
  } else {
    let payload;
    if (action.callback || withValues) {
      const data: T = yield select(getValues, { formName, onlyDirty });
      const dirty: string[] = yield select(getDirtyJS, { formName });
      const structuralChanges: StructuralChange[] = yield select(getStructuralChanges, { formName });
      payload = {
        data,
        dirty,
        structuralChanges
      };
    }

    if (!async) {
      yield put(submitFormSuccess(formName, payload));
    }

    if (action.callback && payload) {
      action.callback(payload);
    }
  }
}

export function* registerFormSaga<T extends Data = Data>(action: RegisterFormAction<T>) {
  const { formName } = action.meta;
  const callbacks = pick(action.payload, [
    'onValidateRef',
    'onChangeFieldRef',
    'onSetFormDataRef',
    'onSubmitRef',
    'validatorRef'
  ]);

  while (true) {
    const { validate, change, unregister } = yield race({
      validate: takeForm(VALIDATE_FORM, formName),
      change: takeForm(
        [CHANGE_FIELD, SET_FORM_DATA, ADD_ARRAY_ITEM, SWAP_ARRAY_ITEMS, REMOVE_ARRAY_ITEM, CHANGE_DISCRIMINATOR_FIELD],
        formName
      ),
      unregister: takeForm(CLEAR_FORM, formName)
    });

    if (unregister) {
      break;
    }

    if (validate) {
      let customErrors = {};
      if (callbacks.validatorRef?.current) {
        const values: T = yield select(getValues, { formName });
        customErrors = callbacks.validatorRef.current(values);
      }

      yield put({
        type: CUSTOM_VALIDATION,
        payload: customErrors,
        meta: { formName }
      });

      const autoSubmit: boolean = yield select(getAutoSubmit, { formName });
      const errors: ImmutableMap<ErrorList> = yield select(getAllErrors, {
        formName
      });
      callbacks.onValidateRef?.current?.(errors.toJS());
      if (autoSubmit && errors.size === 0) {
        const callback = callbacks.onSubmitRef?.current ?? undefined;
        yield put(submitForm({ formName, invokedDuringAutoSubmit: true, callback }));
      }
    } else if (change && (callbacks.onChangeFieldRef?.current || callbacks.onSetFormDataRef?.current)) {
      const data: unknown = yield select(getData, change.meta);
      const dataJs = isIterableObject(data) && typeof data.toJS === 'function' ? data.toJS() : data;
      if (
        change.type === CHANGE_FIELD ||
        change.type === ADD_ARRAY_ITEM ||
        change.type === SWAP_ARRAY_ITEMS ||
        change.type === REMOVE_ARRAY_ITEM ||
        change.type === CHANGE_DISCRIMINATOR_FIELD
      ) {
        const store: ImmutableMap<Data> = yield select(getData, {
          formName: change.meta.formName
        });
        callbacks.onChangeFieldRef?.current?.(change.meta.path, dataJs, store);
      } else if (change.type === SET_FORM_DATA) {
        callbacks.onSetFormDataRef?.current?.(dataJs);
      }
    }
  }
}

export default function* root() {
  yield all([
    takeLatest(
      [...pauseChanges, SUBMIT_FORM, VALIDATE_ON_BLUR, ADD_ARRAY_ITEM, REMOVE_ARRAY_ITEM, CHANGE_DISCRIMINATOR_FIELD],
      validateOnChange
    ),
    takeLatest([BLUR_FIELD], handleBlurField),
    takeLatest(SUBMIT_FORM, submitFormSaga),
    takeFormLatest(REGISTER_FORM, registerFormSaga),
    takeFormLatest(SKIP_REGISTRATION, registerFormSaga)
  ]);
}

export function* waitForFormSubmit(formName: string) {
  while (true) {
    const action: SubmitFormSuccessAction | SubmitFormFailureAction = yield take([
      `${SUBMIT_FORM}_SUCCESS`,
      `${SUBMIT_FORM}_FAILURE`
    ]);
    if (action.meta.formName === formName) {
      return action;
    }
  }
}
